import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Download } from '@mui/icons-material';
import api from '../../services/api';

const itemsPerPage = 10;

const DirectReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [patientName, setPatientName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [page, setPage] = useState(1);

  const fetchReports = async (overrideParams) => {
    setError('');
    try {
      const res = await api.get('/practitioner/direct-reports', {
        params: {
          patientName: overrideParams?.patientName,
          dateOfBirth: overrideParams?.dateOfBirth,
        },
      });
      setReports(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load reports.');
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports({ patientName: '', dateOfBirth: '' });
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      setLoading(true);
      fetchReports({
        patientName: patientName.trim() || undefined,
        dateOfBirth: dateOfBirth || undefined,
      });
      setPage(1);
    }, 300);
    return () => clearTimeout(handle);
  }, [patientName, dateOfBirth]);

  const filtered = useMemo(() => reports, [reports]);

  const paginated = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return filtered.slice(start, start + itemsPerPage);
  }, [filtered, page]);

  const isNewReport = (r) => !r?.practitionerViewedAt && !r?.practitionerDownloadedAt;

  const markViewed = async (reportId) => {
    try {
      await api.patch(`/practitioner/direct-reports/${reportId}/viewed`);
      setReports((prev) =>
        (prev || []).map((r) => (r._id === reportId ? { ...r, practitionerViewedAt: new Date().toISOString() } : r))
      );
    } catch (_) {
      // Non-blocking: highlighting will clear on next refresh/download anyway.
    }
  };

  const handleDownload = async (reportId) => {
    try {
      const response = await api.get(`/practitioner/direct-reports/${reportId}/download`, { responseType: 'blob' });
      const contentDisposition = response.headers?.['content-disposition'] || '';
      const fileNameMatch =
        contentDisposition.match(/filename\*=UTF-8''([^;]+)/i) || contentDisposition.match(/filename="?([^"]+)"?/i);
      const fileName = fileNameMatch?.[1] ? decodeURIComponent(fileNameMatch[1]) : `direct-report-${reportId}`;

      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      // Clear "new" highlight locally (backend also marks downloaded/viewed).
      setReports((prev) =>
        (prev || []).map((r) =>
          r._id === reportId
            ? {
                ...r,
                practitionerViewedAt: r.practitionerViewedAt || new Date().toISOString(),
                practitionerDownloadedAt: r.practitionerDownloadedAt || new Date().toISOString(),
              }
            : r
        )
      );
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to download report.');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <TextField
              label="Patient name"
              size="small"
              value={patientName}
              onChange={(e) => setPatientName(e.target.value)}
              sx={{ minWidth: 240 }}
            />
            <TextField
              label="Date of birth"
              size="small"
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />
            <Button variant="outlined" size="small" onClick={() => fetchReports({ patientName, dateOfBirth })}>
              Refresh
            </Button>
          </Box>
        </Box>

        {error && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Patient</TableCell>
                <TableCell>Date of Birth</TableCell>
                <TableCell>Uploaded</TableCell>
                <TableCell>File</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((r) => (
                <TableRow
                  key={r._id}
                  hover
                  onClick={() => markViewed(r._id)}
                  sx={
                    isNewReport(r)
                      ? {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.12)' },
                        }
                      : undefined
                  }
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight={600}>
                        {r.patientName || '-'}
                      </Typography>
                      {isNewReport(r) && <Chip size="small" color="primary" label="New" />}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {r.dateOfBirth ? (
                      <Chip size="small" label={new Date(r.dateOfBirth).toLocaleDateString()} />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '-'}</TableCell>
                  <TableCell>{r.resultFile?.originalName || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(r._id);
                      }}
                      title="Download"
                    >
                      <Download fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {filtered.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
            No direct reports found for the current filters.
          </Typography>
        )}

        {filtered.length > 0 && (
          <Box sx={{ pt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {((page - 1) * itemsPerPage) + 1} - {Math.min(page * itemsPerPage, filtered.length)} of {filtered.length}
            </Typography>
            <Pagination
              count={Math.ceil(filtered.length / itemsPerPage)}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default DirectReports;

